import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { camelCase, capitalize, isNull, startCase, toString } from 'lodash-es';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {
  ACRONYMNS,
  monthDropDownValues,
  USER_CHECK,
} from '../../core/constants';
import { ColumnProps } from '../types/TableProps';
import { MasterDataService } from '../services/master-data/master-data.service';
import * as XLSX from 'xlsx';
import { validate } from 'uuid';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
const tzMapper: Record<string, string> = {
  'Asia/Calcutta': 'IST',
};
export const convertDateToCommonFormat = (date?: string) => {
  if (!date) {
    return '';
  }
  const localTz = dayjs.tz.guess();
  const formattedDate = dayjs(date)
    .tz(localTz)
    .format(`MMM DD, YYYY hh:mm A ${tzMapper[localTz] || 'z'}`);
  return formattedDate;
};
export const commonDateTimeFormat = (date?: string | Date | null): any => {
  if (!date) {
    return '';
  }
  const localTz = dayjs.tz.guess();
  const dayjsDate = dayjs(date);
  if (dayjsDate.isValid()) {
    const formattedDate = dayjs(date).tz(localTz).format('YYYY-MM-DD hh:mm A');
    return formattedDate;
  }
  return date;
};
export const convertDateToYYMMDD = (date?: string | Date | null): any => {
  if (!date) {
    return '';
  }
  const localTz = dayjs.tz.guess();
  const dayjsDate = date ? dayjs(date) : dayjs();
  if (dayjsDate.isValid()) {
    const formattedDate = dayjsDate.tz(localTz).format('YYYY-MM-DD');
    return formattedDate;
  }
  return date;
};

export const getCurrentDateTime = () => {
  return convertDateToCommonFormat(
    dayjs().utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
  );
};

export const isNumericOrDateField = (value: any): boolean => {
  // Check if the value is a valid date string
  const date = new Date(value);
  const isDate = !isNaN(date.getTime());

  // Check if the value is numeric
  const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);

  return isDate || isNumeric;
};

export const lightenHexColor = (
  hexWithHash: string,
  percent: number
): string => {
  // Remove the hash (#) if it's there
  const hex = hexWithHash.replace(/^#/, '');

  // Convert hex color to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Increase RGB values by the percentage
  r = Math.min(255, Math.floor(r + ((255 - r) * percent) / 100));
  g = Math.min(255, Math.floor(g + ((255 - g) * percent) / 100));
  b = Math.min(255, Math.floor(b + ((255 - b) * percent) / 100));

  // Convert RGB back to hex
  const newHex = `#${r.toString(16).padStart(2, '0')}${g
    .toString(16)
    .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;

  return newHex.toUpperCase();
};

export const extractNameFromUserName = (userName: string): string => {
  console.log('extractNameFromUserName', userName);
  const nameOnly = userName.split('(')[0].trim();

  const [lastName, firstName] = nameOnly.split(',').map((s) => s.trim());

  return `${firstName} ${lastName}`;
};

export const toTitleCase = (str: string) => {
  const acrnInx = ACRONYMNS.has(str.toUpperCase());
  if (acrnInx) {
    return str.toUpperCase();
  }
  return startCase(camelCase(str));
};

export const getFileNameFromS3Url = (url: string) => {
  if (!url) {
    return '';
  }
  // Use the URL constructor to parse the URL
  const parsedUrl = new URL(url);

  // Extract the pathname and split it to get the segments
  const pathSegments = parsedUrl.pathname.split('/');

  // The last segment is the file name
  const fileName = pathSegments[pathSegments.length - 1];

  return isNull(fileName)
    ? 'No Image to display'
    : decodeURIComponent(fileName);
};

export const generateMonthForYear = () => {
  return Object.keys(monthDropDownValues)
    .sort()
    .map((el) => {
      return {
        id: el,
        value: monthDropDownValues[el as keyof typeof monthDropDownValues],
      };
    });
};

export const removeRoleNc = (role: string): string => {
  return role.replace(/^nc-/, '');
};

export const formatRoleName = (role: string): string => {
  if (!role) return '';
  if (role.includes(USER_CHECK.SUPER_ADMIN)) {
    return 'Super Admin';
  }

  // Remove "nc-" prefix and split by hyphen
  let cleanedValue = role.replace(/^nc-/, '').split('-');
  const projectCode = cleanedValue.pop(); // Remove last element (project code)

  // Capitalize each word and join back into a string
  const formattedRole = cleanedValue
    .map((word) => (isNaN(Number(word)) ? capitalize(word) : word))
    .join(' ');

  return formattedRole;
};

export const getWeekOfMonth = (date: Date): number => {
  // Copy the date to avoid modifying the original date
  const currentDate = new Date(date.getTime());

  // Get the first day of the month
  const startOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );

  // Calculate the day of the week for the first day of the month (0: Sunday, 1: Monday, ..., 6: Saturday)
  const startDayOfWeek = startOfMonth.getDay();

  // Calculate the offset from the start of the month
  const offset = (currentDate.getDate() + startDayOfWeek - 1) / 7;

  // Calculate the week number
  return Math.floor(offset) + 1;
};

export const getMonthName = (date: Date): string => {
  return date.toLocaleString('default', { month: 'long' });
};

export const formatThousandNumberToK = (num?: number | null) => {
  if (num === undefined || num === null) {
    return num;
  }

  // Handle millions
  if (num >= 1000000) {
    return `${Number((num / 1000000).toPrecision(4))}M`; // Converts number to millions (e.g., 3,336,454 becomes "3.336M")
  }

  // Handle thousands
  if (num >= 1000) {
    return `${Number((num / 1000).toPrecision(3))}k`; // Converts number to thousands (e.g., 1500 becomes "1.5k")
  }

  // Return number as-is if less than 1000
  return num;
};

export const downloadExportExcel = (
  onDownloadClick: () => Promise<Record<string, any>[]>,
  cols: ColumnProps<any>[],
  appliedColumns: ColumnProps<any>[],
  title: string
) => {
  import('xlsx').then((xlsx) => {
    if (onDownloadClick) {

      onDownloadClick().then((res) => {
        if (cols.some((el) => !!el.children?.length)) {
          // Handle grouped columns
          const parentHeaders: string[] = [];
          const subHeaders: string[] = [];
          const sheetData = [];

          // Traverse columns and create parent and subheaders
          cols.forEach((col) => {
            if (col.children && col.children.length) {
              // Push the parent header with empty subheader placeholders
              parentHeaders.push(col.header);
              col.children.forEach((subCol) => {
                parentHeaders.push(''); // Empty parent placeholder for each child
                subHeaders.push(subCol.header);
              });
            } else {
              parentHeaders.push(col.header);
              subHeaders.push(''); // No sub-header for single column
            }
          });

          // Add headers to sheetData
          sheetData.push(parentHeaders);
          sheetData.push(subHeaders);

          // Map data rows based on grouped columns
          res.forEach((row) => {
            const rowData: any[] = [];
            cols.forEach((col) => {
              if (col.children && col.children.length) {
                col.children.forEach((subCol) => {
                  rowData.push(row[subCol.field as any]);
                });
              } else {
                rowData.push(row[col.field as any]);
              }
            });
            sheetData.push(rowData);
          });

          // Convert to worksheet
          const worksheet = xlsx.utils.aoa_to_sheet(sheetData);
          const workbook = {
            Sheets: { data: worksheet },
            SheetNames: ['data'],
          };
          const excelBuffer = xlsx.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
          });

          // Save the file
          saveAsExcelFile(
            excelBuffer,
            typeof title === 'string' ? title : 'data'
          );
        } else {
          // Flat columns (original logic)
          const tableData = res.map((row) => {
            const obj: Record<string, any> = {};
            appliedColumns.forEach((col) => {
              if (typeof col.field === 'string') {
                if (col.field?.toLowerCase() !== 'action') {
                  obj[col.header] = row[col.field];
                }
              }
            });
            return obj;
          });
          const worksheet = xlsx.utils.json_to_sheet(tableData);
          const workbook = {
            Sheets: { data: worksheet },
            SheetNames: ['data'],
          };
          const excelBuffer = xlsx.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
          });

          saveAsExcelFile(
            excelBuffer,
            typeof title === 'string' ? title : 'data'
          );
        }
      });
    }
  });
};

export const extractFileName = (name: string) => {
  return name.split('images/')[1];
};

export const UrlTrigger = async (key: string, manualFileName: string) => {
  try {
    const response = await fetch(key);
    if (!response.ok) {
      throw new Error('Failed to fetch the file');
    }
    let fileName = manualFileName;

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading the file:', error);
  }
};

export const createAbbreviatedValue = (
  value: string,
  masterDataServiceInstance: MasterDataService
): string => {
  if (!value) {
    return value;
  }
  return value
    .split(' ')
    .map((el) => {
      const abbreviation = masterDataServiceInstance.getAbbreviation(el);
      return abbreviation ? `${el} (${abbreviation})` : el;
    })
    .join(' ');
};

export const saveAsExcelFile = (buffer: BlobPart, fileName: string) => {
  import('file-saver').then((module) => {
    if (module && module.default) {
      const EXCEL_TYPE =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const EXCEL_EXTENSION = '.xlsx';
      const data = new Blob([buffer], {
        type: EXCEL_TYPE,
      });

      module.default.saveAs(
        data,
        fileName + new Date().getTime() + EXCEL_EXTENSION
      );
    }
  });
};

export const convertToFormattedTime = (timeInSeconds: number) => {
  if (!timeInSeconds) {
    return timeInSeconds;
  }
  if (timeInSeconds < 60) {
    return `${timeInSeconds} seconds`;
  }
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  if (minutes < 60) {
    return `${minutes} minutes ${seconds} seconds`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return `${hours} hours ${remainingMinutes} minutes ${seconds} seconds`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return `${days} days ${remainingHours} hours ${remainingMinutes} minutes ${seconds} seconds`;
};

export const emailToName = (email?: string) => {
  if (!email) {
    return '';
  }
  const splitted = email.split('.');
  return (
    toTitleCase(splitted[0]) + ' ' + toTitleCase(splitted[1].split('@')[0])
  );
};

export const sleep = (timeInMs: number) => {
  return new Promise((resolve) => setTimeout(resolve, timeInMs));
};

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));

  return `${value} ${sizes[i]}`;
};

export async function validateManualUploadedFile(
  templateUrl: string,
  file: File
): Promise<{
  isValid: boolean;
  errorType?: 'empty' | 'headersMisMatch' | 'generic';
}> {
  try {
    const templateHeaders = await fetchExcelHeadersFromUrl(templateUrl);

    const fileHeaders = await readExcelHeadersAndCheckEmpty(file);
    if (!fileHeaders) {
      return {
        isValid: false,
        errorType: 'empty',
      };
    }

    const areEqual = setsEqual(templateHeaders, fileHeaders);
    if (!areEqual) {
      return {
        isValid: false,
        errorType: 'headersMisMatch',
      };
    }
    return {
      isValid: true,
    };
  } catch (error) {
    console.error('Error processing Excel files:', error);
    return {
      isValid: false,
      errorType: 'generic',
    };
  }
}

async function fetchExcelHeadersFromUrl(url: string): Promise<Set<string>> {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  return extractHeadersFromExcel(arrayBuffer);
}

function extractHeadersFromExcel(arrayBuffer: ArrayBuffer): Set<string> {
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  const sheet = workbook.Sheets[sheetName];
  const json: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

  const headerRow = json[0] || [];
  return new Set(
    headerRow
      .filter(
        (header) =>
          header !== undefined &&
          header !== null &&
          header.toString().trim() !== ''
      )
      .map((header) => header.toString().trim().toLowerCase())
  );
}

/**
 * Reads Excel file, returns headers set or null if the file is empty.
 */
async function readExcelHeadersAndCheckEmpty(
  file: File
): Promise<Set<string> | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const arrayBuffer = event.target!.result as ArrayBuffer;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        if (workbook.SheetNames.length === 0) {
          return resolve(null);
        }

        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        if (
          !json ||
          json.length <= 1 ||
          json.slice(1).every((row) =>
            row.every((cell) => {
              return !toString(cell)?.trim();
            })
          )
        ) {
          return resolve(null); // empty or header-only file
        }

        const headerRow = json[0];
        const headers = new Set(
          headerRow
            .filter(
              (header) =>
                header !== undefined &&
                header !== null &&
                header.toString().trim() !== ''
            )
            .map((header) => header.toString().trim().toLowerCase())
        );

        if (headers.size === 0) {
          return resolve(null);
        }

        resolve(headers);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = (err) => reject(err);
    reader.readAsArrayBuffer(file);
  });
}

function setsEqual(set1: Set<string>, set2: Set<string>): boolean {
  return set1.size === set2.size && [...set1].every((item) => set2.has(item));
}

export const clipUuidFromFile = (fileName: string) => {
  const sliced = fileName.slice(0, 36);
  if (validate(sliced)) {
    return fileName.slice(37);
  }
  return fileName;
};

export const replaceTokens = (
  template: string,
  values: Record<string, string | number | undefined>
): string => {
  return template.replace(
    /{{(.*?)}}/g,
    (_, token) => values[token.trim()]?.toString() || ''
  );
};
